import type {
	InspectionTemplate,
	InspectionTemplateSection,
} from "@/shared/models/Inspection";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
	alpha,
	Box,
	Chip,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import type { ICellRendererParams } from "ag-grid-community";

const statusOf = (template: InspectionTemplate) => {
	if (template.IsCurrentRev)
		return { label: "Current", tone: "success" as const, isOpen: true };

	if (
		template.IsLatest &&
		template.EffectiveFrom !== null &&
		template.EffectiveFrom > new Date()
	)
		return { label: "Scheduled", tone: "info" as const, isOpen: true };

	return { label: "Superseded", tone: "neutral" as const, isOpen: false };
};

type PanelProps = ICellRendererParams<InspectionTemplateSection> & {
	revisions: InspectionTemplate[];
	onEdit: (template: InspectionTemplate) => void;
	onView: (template: InspectionTemplate) => void;
	onDuplicate: (template: InspectionTemplate) => void;
	onDelete: (template: InspectionTemplate) => void;
};

export const TemplateRevisionsPanel = ({
	data,
	revisions,
	onEdit,
	onView,
	onDuplicate,
	onDelete,
}: PanelProps) => {
	const sectionRevisions = revisions.filter(
		(template) => template.SectionId === data?.SectionId && template.IsActive,
	);

	if (sectionRevisions.length === 0)
		return (
			<Box sx={{ px: 5, py: 1.5 }}>
				<Typography variant="body2" color="text.secondary">
					{`${data?.SectionName ?? "This section"} has no sheet yet. Write its first
					revision from the section row.`}
				</Typography>
			</Box>
		);

	return (
		<Box sx={{ px: 5, py: 0.5 }}>
			{sectionRevisions.map((template) => {
				const status = statusOf(template);

				return (
					<Box
						key={template.TemplateId}
						onClick={() =>
							status.isOpen ? onEdit(template) : onView(template)
						}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							py: 0.5,
							borderBottom: "1px solid",
							borderColor: "divider",
							"&:last-of-type": { borderBottom: "none" },
							cursor: "pointer",
							"&:hover": { backgroundColor: "action.hover" },
						}}
					>
						<Typography
							sx={{ fontWeight: 600, minWidth: 60 }}
							variant="caption"
						>
							{`Rev ${template.Rev}`}
						</Typography>

						<Chip
							size="small"
							label={status.label}
							sx={{
								height: 22,
								fontSize: 12,
								fontWeight: 600,
								border: "none",
								color: (theme) =>
									status.tone === "neutral"
										? theme.palette.text.secondary
										: theme.palette[status.tone].main,
								backgroundColor: (theme) =>
									alpha(
										status.tone === "neutral"
											? theme.palette.text.secondary
											: theme.palette[status.tone].main,
										0.1,
									),
								minWidth: 90,
							}}
						/>

						<Typography variant="body2" color="text.secondary">
							{`from ${dxtToLocalServerTime(template.EffectiveFrom, "MM/dd/yyyy")} · ${template.ColumnCount} columns · ${
								template.InspectionCount
							} inspections`}
						</Typography>

						<Box sx={{ flex: 1 }} />

						<Box
							sx={{ display: "flex", alignItems: "center" }}
							onClick={(event) => event.stopPropagation()}
						>
							{status.isOpen ? (
								<>
									<Tooltip title="Edit revision">
										<IconButton size="small" onClick={() => onEdit(template)}>
											<EditIcon fontSize="small" color="primary" />
										</IconButton>
									</Tooltip>
									<Tooltip title="Duplicate for another section">
										<IconButton
											size="small"
											onClick={() => onDuplicate(template)}
										>
											<ContentCopyIcon fontSize="small" color="primary" />
										</IconButton>
									</Tooltip>
									{template.IsLatest && (
										<Tooltip title="Delete revision">
											<IconButton
												size="small"
												onClick={() => onDelete(template)}
											>
												<DeleteIcon fontSize="small" color="secondary" />
											</IconButton>
										</Tooltip>
									)}
								</>
							) : (
								<Tooltip title="A newer revision has taken over, so this sheet stays as it was read. Open the row to see what it says.">
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											color: "text.disabled",
											px: 1,
										}}
									>
										<IconButton size="small" onClick={() => {}}>
											<LockOutlinedIcon fontSize="small" color="disabled" />
										</IconButton>
									</Box>
								</Tooltip>
							)}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
};
